import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, defer, iif, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ActionResultCompanyRegistrationResponse } from '@portal/models/actionResultCompanyRegistrationResponse';
import { ActionResultUserAuthenticateResponse } from '@portal/models/actionResultUserAuthenticateResponse';
import { RoleSaveRequest } from '@portal/models/roleSaveRequest';
import { getBuyerEndpoint, registrationClientContext } from '@portal/api-endpoints';
import { AGENT_TYPE } from '../auth.constants';
import { AuthService } from '../auth.service';
import { clientContext } from '../client.context';
import { TermsOfUseContext, TermsOfUseService } from '../terms-of-use/terms-of-use.service';
import ProfileTypeEnum = RoleSaveRequest.ProfileTypeEnum;

const TERM_OF_USE_NOT_ACCEPTED = 'TERM_OF_USE_NOT_ACCEPTED';
const HTTP_FORBIDDEN = 403;

@Injectable()
export class TermsOfUseInterceptor implements HttpInterceptor {
  private readonly tokenEndpoint = clientContext.token;

  constructor(
    private readonly termsService: TermsOfUseService,
    private readonly auth: AuthService,
    private readonly injector: Injector,
  ) {}

  private get buyerBaseUrl(): string {
    const agentType = this.injector.get<ProfileTypeEnum>(AGENT_TYPE);

    return getBuyerEndpoint(agentType) ?? '';
  }

  intercept<T>(request: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    return next.handle(request).pipe(
      switchMap((event: HttpEvent<T>) => {
        if (
          event instanceof HttpResponse &&
          this.isTokenEndpoint(request.url) &&
          (event.body as unknown as ActionResultUserAuthenticateResponse)?.value?.shouldAcceptNewTerms
        ) {
          const body = (event.body as unknown as ActionResultUserAuthenticateResponse).value;
          const ctx: TermsOfUseContext = {
            userId: body.userId,
            termsPagePath: body.termsOfUsePagePath,
            buyerBaseUrl: this.buyerBaseUrl,
          };

          this.auth.setTokenAndPrivileges(body);

          return iif(
            () => this.termsService.isPending,
            this.termsService.waitForResolution(),
            defer(() => this.termsService.handleTermsRequired(ctx, { skipRenew: true })),
          ).pipe(switchMap(() => of(event)));
        }

        if (
          event instanceof HttpResponse &&
          this.isRegistrationThirdStageEndpoint(request.url) &&
          (event.body as unknown as ActionResultCompanyRegistrationResponse)?.value?.userAuthenticateResponse
            ?.shouldAcceptNewTerms
        ) {
          const userAuthResponse = (event.body as unknown as ActionResultCompanyRegistrationResponse).value
            .userAuthenticateResponse;
          const ctx: TermsOfUseContext = {
            userId: userAuthResponse.userId,
            termsPagePath: userAuthResponse.termsOfUsePagePath,
            buyerBaseUrl: this.buyerBaseUrl,
          };

          this.auth.setTokenAndPrivileges(userAuthResponse);

          return iif(
            () => this.termsService.isPending,
            this.termsService.waitForResolution(),
            defer(() => this.termsService.handleTermsRequired(ctx, { skipRenew: true, skipPersist: true })),
          ).pipe(switchMap(() => of(event)));
        }

        return of(event);
      }),
      catchError((err: HttpErrorResponse) => {
        if (err.status === HTTP_FORBIDDEN && err.error?.errorCode === TERM_OF_USE_NOT_ACCEPTED) {
          const ctx: TermsOfUseContext = {
            termsPagePath: err.error?.value as string | undefined,
            buyerBaseUrl: this.buyerBaseUrl,
          };

          return iif(
            () => this.termsService.isPending,
            this.termsService.waitForResolution(),
            defer(() => this.termsService.handleTermsRequired(ctx)),
          ).pipe(switchMap(() => next.handle(this.addToken(request))));
        }

        return throwError(() => err);
      }),
    );
  }

  private isTokenEndpoint(url: string): boolean {
    return url.includes(this.tokenEndpoint);
  }

  private isRegistrationThirdStageEndpoint(url: string): boolean {
    return url.includes(registrationClientContext.thirdStage);
  }

  private addToken<T>(request: HttpRequest<T>): HttpRequest<T> {
    const authHeader = this.auth.getAuthHeader();

    if (!authHeader) {
      return request;
    }

    return request.clone({ setHeaders: { Authorization: authHeader } });
  }
}
